<template>
    <section
        v-loading="m__loading"
        class="component-box !rounded-br-none relative"
    >
        <div class="flex space-x-3 overflow-x-auto pb-2">
            <div
                v-for="(item, index) in p__trend_basic_chart_options"
                :key="index"
                class="w-[160px] flex-shrink-0 bg-bg rounded-lg flex overflow-hidden cursor-pointer"
                :class="[
                    hasSelectedTrend(item.value)
                        ? ''
                        : 'opacity-70 hover:opacity-800 group'
                ]"
                @click="handleChangeSelectedTrend(item.value)"
            >
                <div
                    class="w-1 h-full"
                    :style="{
                        backgroundColor: getColorByTrend(item.value)
                    }"
                    :class="[
                        hasSelectedTrend(item.value)
                            ? ''
                            : 'invisible group-hover:visible'
                    ]"
                ></div>
                <div class="ml-4 py-2">
                    <div class="flex space-x-1 items-center">
                        <p class="text-[0.75rem] font-semibold">
                            {{ p__getNameByTrend(item.value) }}
                        </p>
                        <el-tooltip effect="dark" placement="top">
                            <div slot="content" class="max-w-[360px]">
                                <p
                                    v-html="
                                        $t(
                                            `page.campaign.description_column_${item.value}`
                                        )
                                    "
                                ></p>
                            </div>

                            <i
                                class="el-icon-info text-desc-text text-[0.85rem]"
                            ></i>
                        </el-tooltip>
                    </div>

                    <p
                        :style="{ color: getColorByTrend(item.value) }"
                        class="text-[0.95rem] font-semibold mt-1"
                    >
                        {{
                            m__formatterColumn(
                                total ? total[item.value] : 0,
                                item.value,
                                current_currency
                            )
                        }}
                    </p>
                    <span
                        v-if="compare_ranger_date"
                        class="text-[0.8rem] font-semibold text-desc-text"
                    >
                        {{
                            m__formatterColumn(
                                compare_total ? compare_total[item.value] : 0,
                                item.value,
                                current_currency
                            )
                        }}
                    </span>
                </div>
            </div>
        </div>
        <div v-show="show_chart" class="mt-4 relative">
            <Chart :options="chart_options"></Chart>

            <div
                v-if="!m__loading && !chart.length"
                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            >
                <div class="util-flex-center flex-col">
                    <img
                        class="w-[140px] h-[140px]"
                        :src="require('@/assets/images/no_data.png')"
                    />
                    <p class="mt-2 text-desc-text text-[0.9rem]">
                        {{ $t('common.no_data') }}
                    </p>
                </div>
            </div>
        </div>

        <div
            class="absolute -bottom-9 right-0 h-9 w-12 bg-white rounded-b-lg z-40 util-flex-center cursor-pointer group"
            @click="show_chart = !show_chart"
        >
            <i
                v-if="show_chart"
                class="el-icon-top group-hover:scale-125 transition duration-300"
            ></i>
            <i
                v-else
                class="el-icon-bottom group-hover:scale-125 transition duration-300"
            ></i>
        </div>
    </section>
</template>

<script>
import { getReportOverview } from '@/services/atosa-tiktok-ads/reporting'
import { getAdvertiserAccountDetail } from '@/services/atosa-tiktok-ads/advertiser-account'
import { Chart } from 'highcharts-vue'
import campaignMixin from '@/views/campaign/_mixin'

const renderYAxis = () => {
    return {
        title: {
            text: ''
        },
        labels: {
            enabled: false
        },
        visible: true
    }
}

export default {
    components: {
        Chart
    },

    mixins: [campaignMixin],

    props: [
        'ranger_date',
        'advertiser_id',
        'tiktok_account_id',
        'filtering',
        'compare_ranger_date'
    ],

    data() {
        return {
            show_chart: true,
            chart_options: {
                chart: {
                    type: 'spline',
                    height: 240
                },
                title: {
                    text: ''
                },
                legend: {
                    enabled: false,
                    floating: true
                },
                xAxis: {
                    categories: [],
                    crosshair: true,
                    minTickInterval: 6,
                    tickInterval: 1,
                    endOnTick: true,
                    startOnTick: true
                },
                tooltip: {
                    pointFormatter: null,
                    shared: true
                },
                yAxis: [],
                series: []
            },
            current_currency: 'USD',
            total: null,
            compare_total: null,
            chart: [],
            selected_trends: [],
            value: ''
        }
    },

    watch: {
        advertiser_id() {
            this.fetchDetailAdsAccount()

            this.fetchOverviewReport()
        },

        filtering() {
            this.fetchOverviewReport()
        },

        ranger_date() {
            this.fetchOverviewReport()
        },

        compare_ranger_date() {
            if (this.compare_ranger_date) {
                this.fetchOverviewCompareReport()
            } else {
                this.compare_total = null
            }
        },

        selected_trends() {
            this.changeSeriesChart()
        },

        show_chart() {
            localStorage.setItem(
                `storage_show_chart`,
                JSON.stringify(this.show_chart)
            )
        }
    },

    mounted() {
        this.getSelectedTrendsFromStorage()

        this.getShowChartFromStorage()

        const m__formatterColumn = (x, y) =>
            this.m__formatterColumn(x, y, this.current_currency)
        const p__getNameByTrend = this.p__getNameByTrend
        this.chart_options.tooltip.pointFormatter = function () {
            const point = this
            const series = point.series
            const y = m__formatterColumn(point.y || 0, series.name)
            return `${p__getNameByTrend(series.name)}: <b>${y}</b><br/>`
        }
    },

    methods: {
        getSelectedTrendsFromStorage() {
            const temp = JSON.parse(
                localStorage.getItem(`storage_selected_trends`)
            )

            if (temp && temp.length) {
                this.selected_trends = temp
            } else {
                this.selected_trends = this.p__trend_basic_chart_options
                    .slice(0, 4)
                    .map((trend) => trend.value)
            }
        },

        getShowChartFromStorage() {
            const temp = JSON.parse(localStorage.getItem(`storage_show_chart`))

            if (temp !== null) {
                this.show_chart = temp
            }
        },

        async fetchOverviewReport() {
            if (!this.tiktok_account_id || !this.advertiser_id) {
                return
            }

            this.m__loading = true

            try {
                const response = await getReportOverview(
                    this.tiktok_account_id,
                    {
                        start_date: this.ranger_date[0],
                        end_date: this.ranger_date[1],
                        // filtering: this.filtering,
                        advertiser_id: this.advertiser_id
                    }
                )

                if (response.code === 0) {
                    this.total = response.data.total
                    this.chart = response.data.chart

                    const formatDate =
                        this.ranger_date[0] === this.ranger_date[1]
                            ? this.$t('common.format_fulldate')
                            : this.$t('common.format_date')
                    this.chart_options.xAxis.categories = this.chart.map(
                        (item) =>
                            this.p__formatDate(item.start_time, formatDate)
                    )

                    this.changeSeriesChart()
                }
            } catch (error) {
                console.error(error)
            }

            this.m__loading = false
        },

        async fetchOverviewCompareReport() {
            if (!this.tiktok_account_id || !this.advertiser_id) {
                return
            }

            try {
                const response = await getReportOverview(
                    this.tiktok_account_id,
                    {
                        start_date: this.compare_ranger_date[0],
                        end_date: this.compare_ranger_date[1],
                        // filtering: this.filtering,
                        advertiser_id: this.advertiser_id
                    }
                )

                this.compare_total = response.data.total
            } catch (error) {
                console.error(error)
            }
        },

        hasSelectedTrend(value) {
            const trend = this.selected_trends.find((item) => item === value)

            if (trend) {
                return true
            }
            return false
        },

        getColorByTrend(value) {
            const trend = this.p__trend_basic_chart_options.find(
                (item) => item.value === value
            )

            if (trend) {
                return trend.color
            }

            return 'red'
        },

        handleChangeSelectedTrend(value) {
            const index = this.selected_trends.findIndex(
                (item) => item === value
            )
            if (index > -1) {
                if (this.selected_trends.length === 1) {
                    return
                }
                this.selected_trends.splice(index, 1)
            } else {
                this.selected_trends = [...this.selected_trends, value]
            }

            localStorage.setItem(
                `storage_selected_trends`,
                JSON.stringify(this.selected_trends)
            )
        },

        changeSeriesChart() {
            const series = []
            const yAxis = []
            let index = 0

            for (const item of this.selected_trends) {
                const trend = this.p__trend_basic_chart_options.find(
                    (t) => t.value === item
                )

                const data = this.chart.map((it) => +it[item])

                series.push({
                    name: trend ? trend.value : '',
                    color: trend ? trend.color : 'red',
                    data,
                    yAxis: index,
                    marker: {
                        enabled: false,
                        states: {
                            hover: {
                                enabled: false
                            }
                        }
                    }
                })

                yAxis.push(renderYAxis())

                index++
            }

            this.chart_options.series = series

            this.chart_options.yAxis = yAxis
        },

        async fetchDetailAdsAccount() {
            if (!this.tiktok_account_id || !this.advertiser_id) {
                return
            }
            const response = await getAdvertiserAccountDetail(
                this.tiktok_account_id,
                [this.advertiser_id]
            )

            if (
                response.code === 0 &&
                response.data.length &&
                response.data[0].currency
            ) {
                this.current_currency = response.data[0].currency
            }
        }
    }
}
</script>
